import { useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

export interface ConfirmDialogOptions {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Renders the confirm button in destructive (red) styling. */
  destructive?: boolean;
}

export interface ConfirmDialogProps extends ConfirmDialogOptions {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
}

/**
 * Controlled confirm dialog. Prefer the `useConfirm()` hook below
 * for one-shot prompts where you don't want to thread `open` /
 * `setOpen` state through the calling component.
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
}: ConfirmDialogProps) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description ? (
          <AlertDialogDescription>{description}</AlertDialogDescription>
        ) : null}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>{cancelLabel}</AlertDialogCancel>
        <AlertDialogAction
          onClick={() => void onConfirm()}
          className={cn(
            destructive &&
              'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/40',
          )}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

/**
 * Imperative hook returning a `confirm({...}) => Promise<boolean>`
 * function plus the `<dialog>` JSX you render once. Mirrors the
 * shape of `window.confirm` so swapping callsites is a one-line
 * edit:
 *
 *     // before
 *     if (!window.confirm('Delete this comment?')) return;
 *     await onDelete();
 *
 *     // after
 *     const { confirm, dialog } = useConfirm();
 *     // ... in JSX: {dialog}
 *     if (!(await confirm({ title: 'Delete this comment?', destructive: true }))) return;
 *     await onDelete();
 *
 * Returns `true` on confirm, `false` on cancel / dismiss / esc /
 * click-outside. The Promise resolves only once per `confirm()`
 * call; subsequent invocations open a fresh dialog.
 */
export const useConfirm = () => {
  const [state, setState] = useState<ConfirmDialogOptions | null>(null);
  const resolverRef = useRef<((result: boolean) => void) | null>(null);

  const confirm = useCallback(
    (options: ConfirmDialogOptions): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        // If a previous confirm is somehow still pending (mid-tick
        // double-click on the same trigger), resolve it as cancel
        // before starting a new one.
        if (resolverRef.current) resolverRef.current(false);
        resolverRef.current = resolve;
        setState(options);
      }),
    [],
  );

  const handleOpenChange = useCallback((open: boolean) => {
    if (!open) {
      // Esc / click-outside / cancel — resolve as `false`.
      const resolver = resolverRef.current;
      resolverRef.current = null;
      setState(null);
      resolver?.(false);
    }
  }, []);

  const handleConfirm = useCallback(() => {
    const resolver = resolverRef.current;
    resolverRef.current = null;
    setState(null);
    resolver?.(true);
  }, []);

  const dialog = state ? (
    <ConfirmDialog
      open={true}
      onOpenChange={handleOpenChange}
      onConfirm={handleConfirm}
      {...state}
    />
  ) : null;

  return { confirm, dialog };
};
